import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/assets/images/logo.jpeg";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/what-we-do", label: "What We Do" },
  { path: "/who-we-serve", label: "Who We Serve" },
  { path: "/veteran-support", label: "Veteran Support" },
  { path: "/why-this-matters", label: "Why This Matters" },
  { path: "/workforce-empowerment", label: "Workforce Empowerment" },
  { path: "/blog", label: "Blog" },
];

const ctaLinks = [
  { path: "/request-support", label: "Request Support" },
  { path: "/partner-with-us", label: "Partner With Us" },
  { path: "/contact", label: "Contact" },
];

const legalLinks = [
  { path: "/legal-policy", label: "Legal Policy" },
  { path: "/privacy-policy", label: "Privacy Policy" },
];

const relatedInitiatives = [
  {
    name: "His Presence Chapel Inc.",
    url: "https://hispresencechapel.org/",
  },
  {
    name: "RAHVAI",
    url: "https://rahvai.org/",
  },
  {
    name: "SEEAL Africa",
    url: "https://seeal.africa/",
  },
];

const PublicLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-brand-neutral">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="BHNA Logo" className="h-14 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    isActive(link.path)
                      ? "text-brand-orange bg-brand-orange/10"
                      : "text-brand-blue hover:text-brand-orange hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/request-support">
                <Button
                  data-testid="nav-request-support-btn"
                  className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-5 py-2 text-sm font-semibold"
                >
                  Request Support
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="nav-contact-btn"
                  variant="outline"
                  className="border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white rounded-full px-5 py-2 text-sm font-semibold"
                >
                  Contact Us
                </Button>
              </Link>
            </div>

            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-4 space-y-2">
              {[...navLinks, ...ctaLinks, ...legalLinks].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive(link.path)
                      ? "text-brand-orange bg-brand-orange/10"
                      : "text-brand-blue hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-brand-blue text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* About */}
            <div className="lg:col-span-2">
              <img
                src={LOGO_URL}
                alt="BHNA Logo"
                className="h-16 w-auto mb-4 bg-white rounded-lg p-2"
              />
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Behavioral Health Navigation & Workforce Agency is an initiative of
                His Presence Chapel Inc., a registered 501(c)(3) nonprofit
                organization in Texas, United States. The agency focuses on
                strengthening access to behavioral health support systems through
                navigation assistance, community engagement, and workforce
                empowerment initiatives.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navLinks.slice(0, 5).map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/80 hover:text-brand-teal text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/contact"
                    className="text-white/80 hover:text-brand-teal text-sm transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-bold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <MapPin size={18} className="text-brand-teal mt-0.5 flex-shrink-0" />
                  <span className="text-white/80 text-sm">Cypress, Texas 77433, USA</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Phone size={18} className="text-brand-teal flex-shrink-0" />
                  <a
                    href="tel:+18328878254"
                    className="text-white/80 hover:text-brand-teal text-sm transition-colors"
                  >
                    +1 (832) 887-8254
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail size={18} className="text-brand-teal flex-shrink-0" />
                  <a
                    href="mailto:info@behavioralhealthnavigation.org"
                    className="text-white/80 hover:text-brand-teal text-sm transition-colors whitespace-nowrap"
                  >
                    info@behavioralhealthnavigation.org
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Related Initiatives */}
          <div className="border-t border-white/20 mt-12 pt-8">
            <h4 className="font-heading font-bold text-lg mb-4">Related Initiatives</h4>
            <div className="flex flex-wrap gap-4">
              {relatedInitiatives.map((initiative) => (
                <a
                  key={initiative.name}
                  href={initiative.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/85 hover:bg-white hover:text-brand-blue transition-all"
                >
                  {initiative.name}
                  <ExternalLink size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <Link
                to="/legal-policy"
                className="text-white/75 hover:text-brand-teal transition-colors"
              >
                Legal Policy
              </Link>
              <Link
                to="/privacy-policy"
                className="text-white/75 hover:text-brand-teal transition-colors"
              >
                Privacy Policy
              </Link>
            </div>

            <p className="text-white/60 text-sm">
              &copy; {new Date().getFullYear()} Behavioral Health Navigation & Workforce Agency. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;